import { ArrowUpRight, Search } from 'lucide-react';
import { type ReactNode, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import type { RowAction } from '@/components/ui/row-actions-menu';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AddFilterMenu } from './AddFilterMenu';
import { FilterChip } from './FilterChip';
import { FiltersSheet, type ViewLink } from './FiltersSheet';
import { optionLabel, type FilterField, type FilterOption } from './filters';
import { ListMoreMenu } from './ListMoreMenu';
import { MonthPicker } from './MonthPicker';
import { PageSizeSelect } from './PageSizeSelect';
import type { PageSize } from './use-list-params';
import { useNarrow } from './use-narrow';
import { useSearchField } from './use-search-field';
import { ViewTabs, type ListView } from './ViewTabs';

function ToolbarSearch({
  label,
  typed,
  onType,
  touch = false,
}: {
  readonly touch?: boolean;
  readonly label: string;
  readonly typed: string;
  readonly onType: (value: string) => void;
}) {
  const searchId = useId();
  return (
    <div className="list-toolbar-search">
      <label htmlFor={searchId} className="sr-only">
        {label}
      </label>
      <Search
        aria-hidden="true"
        className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
      />
      <Input
        id={searchId}
        type="search"
        value={typed}
        onChange={(event) => onType(event.target.value)}
        placeholder={label}
        className={touch ? 'min-h-11 pl-9' : 'pl-9'}
      />
    </div>
  );
}

function SortSelect({
  sort,
}: {
  readonly sort: {
    readonly label: string;
    readonly value: string;
    readonly options: readonly FilterOption[];
    readonly onChange: (value: string) => void;
  };
}) {
  return (
    <Select value={sort.value} onValueChange={sort.onChange}>
      <SelectTrigger aria-label={sort.label} className="list-toolbar-chip">
        <span className="text-muted-foreground">{sort.label}:</span>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {sort.options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

/**
 * The entries that leave the list for another page, on the views' row: styled as the other views,
 * marked as a way out, so every view the person has sits in one control (#952 M4).
 */
function ViewLinks({ links }: { readonly links: readonly ViewLink[] }) {
  return links.map((link) =>
    link.to === undefined ? (
      <button
        key={link.id}
        type="button"
        onClick={link.onSelect}
        className="text-muted-foreground hover:bg-muted focus-visible:ring-ring inline-flex min-h-11 shrink-0 items-center gap-1 rounded-md px-3 text-sm whitespace-nowrap focus-visible:ring-2 focus-visible:outline-none"
      >
        {link.label}
        <ArrowUpRight aria-hidden="true" className="size-3.5" />
      </button>
    ) : (
      <Link
        key={link.id}
        to={link.to}
        className="text-muted-foreground hover:bg-muted focus-visible:ring-ring inline-flex min-h-11 shrink-0 items-center gap-1 rounded-md px-3 text-sm whitespace-nowrap focus-visible:ring-2 focus-visible:outline-none"
      >
        {link.label}
        <ArrowUpRight aria-hidden="true" className="size-3.5" />
      </Link>
    ),
  );
}

/**
 * The shared filterable list's toolbar (decision 101, #401, the boards approved in decision 105).
 *
 * - `scope` («Mostra»), when given, on its own row; the named views under it (#952 M2).
 * - One row for the rest (#952 M1): the search, the period, each filter in force as a label that
 *   removes itself, «+ Filtro» to add one, «Azzera i filtri» whenever anything narrows the list,
 *   and the order at the end.
 *
 * It holds no state of its own beyond the typed search: the caller keeps search, filters and order
 * in the URL (`useListParams`) and passes them in.
 */
export function ListToolbar({
  label,
  search,
  fields,
  active,
  onFilter,
  onClear,
  sort,
  views,
  pageSize,
  viewsAside,
  scope,
  more,
}: {
  /** Names the toolbar for assistive technology, usually the list's own title. */
  readonly label: string;
  readonly search: {
    readonly label: string;
    readonly value: string;
    readonly onWrite: (q: string) => void;
  };
  readonly fields: readonly FilterField[];
  readonly active: Readonly<Record<string, string | null>>;
  readonly onFilter: (key: string, value: string | null) => void;
  readonly onClear: () => void;
  readonly sort?: {
    readonly label: string;
    readonly value: string;
    readonly options: readonly FilterOption[];
    readonly onChange: (value: string) => void;
  };
  /** Optional bar of named views above the search; absent, nothing changes. */
  readonly views?: {
    readonly label: string;
    readonly items: readonly ListView[];
    readonly current: { get: (key: string) => string | null };
    readonly onSelect: (view: ListView) => void;
    /** Phone only: entries that open another page, listed in the «Vista» select after the views. */
    readonly links?: readonly ViewLink[] | undefined;
  };
  /** Sits with the views, on their row (actions on the views, such as saving one). */
  readonly viewsAside?: ReactNode;
  /** Whose records the list shows («Mostra»): its own row above the views, wide; in the sheet, narrow. */
  readonly scope?: ReactNode;
  /** Optional page-size choice next to the order; absent, nothing changes. */
  readonly pageSize?:
    | {
        readonly value: PageSize;
        readonly onChange: (size: PageSize) => void;
      }
    | undefined;
  /**
   * What the phone keeps in a «···» menu (import, exports…); the wide screen shows those
   * elsewhere, so it ignores this.
   */
  readonly more?: readonly RowAction[] | undefined;
}) {
  const { t } = useTranslation('common');
  const [typed, setTyped] = useSearchField(search.value, search.onWrite);
  const regularFields = fields.filter((field) => !field.monthPicker);
  const inForce = regularFields.filter((field) => active[field.key]);
  const narrowed = search.value !== '' || fields.some((field) => active[field.key]);
  const narrow = useNarrow();

  if (narrow) {
    // #956: the first screen shows records, so the controls fold into one button and one menu.
    return (
      <div
        role="toolbar"
        aria-label={label}
        className="list-toolbar list-toolbar-stacked max-w-full min-w-0"
      >
        <ToolbarSearch label={search.label} typed={typed} onType={setTyped} touch />
        <div className="list-toolbar-row flex-nowrap">
          <FiltersSheet
            label={label}
            count={inForce.length}
            sort={sort}
            views={views}
            viewsAside={
              scope === undefined ? (
                viewsAside
              ) : (
                <>
                  {scope}
                  {viewsAside}
                </>
              )
            }
            fields={fields}
            active={active}
            onFilter={onFilter}
            onClear={onClear}
            narrowed={narrowed}
            pageSize={pageSize}
          />
          <ListMoreMenu actions={more ?? []} />
        </div>
      </div>
    );
  }

  return (
    <div
      role="toolbar"
      aria-label={label}
      className="list-toolbar list-toolbar-stacked max-w-full min-w-0"
    >
      {scope ? <div className="list-toolbar-row">{scope}</div> : null}
      {views ? (
        <div className="list-toolbar-row max-w-full min-w-0">
          <ViewTabs
            label={views.label}
            views={views.items}
            current={views.current}
            onSelect={views.onSelect}
          />
          {views.links ? <ViewLinks links={views.links} /> : null}
          {viewsAside}
        </div>
      ) : null}
      <div className="list-toolbar-row list-toolbar-filters">
        <ToolbarSearch label={search.label} typed={typed} onType={setTyped} />
        {fields
          .filter((field) => field.monthPicker)
          .map((field) => (
            <MonthPicker
              key={field.key}
              label={field.label}
              value={active[field.key] ?? null}
              onChange={(value) => onFilter(field.key, value)}
            />
          ))}
        {inForce.map((field) => (
          <FilterChip
            key={field.key}
            label={field.label}
            value={optionLabel(field, active[field.key] ?? '')}
            onRemove={() => onFilter(field.key, null)}
          />
        ))}
        {regularFields.length > 0 ? (
          <AddFilterMenu fields={regularFields} active={active} onChoose={onFilter} />
        ) : null}
        {/* Whenever something narrows the list, not only when nothing is left. */}
        {narrowed ? (
          <Button
            variant="ghost"
            size="sm"
            className="min-h-11 px-2 text-[13.5px]"
            onClick={onClear}
          >
            {t('list.clear')}
          </Button>
        ) : null}
        <span className="list-toolbar-spacer" />
        {sort ? <SortSelect sort={sort} /> : null}
        {pageSize ? <PageSizeSelect value={pageSize.value} onChange={pageSize.onChange} /> : null}
      </div>
    </div>
  );
}
